import { AnimatePresence, motion } from "framer-motion";
import { useContext, useEffect, useState } from "react";
import { FaCheckCircle, FaSave } from "react-icons/fa";
import { PiArrowBendUpLeft } from "react-icons/pi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuditObjectTypes, RoleResponseModel } from "../../_core/_model.ts";
import { AddRemoveUsersRoleGroup, GerUsersRoleGroup, GetUserRoles, GetUsers, UpdateUserRoles } from "../../_core/_request.ts";
import { allAccessRoleID, IsAdminUser } from "../../Constants.ts";
import { useAppContext } from "../../context/AppContext.tsx";
import { AuthContext } from "../../context/AuthContext.js";
import { useToast } from "../../context/ToastContext.tsx";
import { defaultTheme } from '../../styles/themes/defaultTheme.js';
import { useJoyride } from "../../UIComponents/JoyrideTour/JoyrideContext.tsx";
import SavingIndicator from "../../UIComponents/SavingIndicator.tsx";
import UserTypeBadge from "../../UIComponents/UserTypeBadge.tsx";
import { AreObjectsEquals, GetObjectDifferences } from "../../Utilities/Utilities.ts";
import UserColumnSecurity from "../Analytics/ColumnRestrictions/UserColumnSecurity.tsx";
import UserReports from "../Analytics/UserReports.tsx";
import { UserFormRequest } from "../Forms/UserRequest/_core/_models.ts";
import { UpsertUser } from "../Forms/UserRequest/_core/_request.ts";
import ComponentLayout from "../Layouts/ComponentLayout.tsx";
import { RoleGroup } from "../Roles/_core/_model.ts";
import RoleTabs from "../Roles/RoleTabs.tsx";
import TooltipButton from "../Tools/TooltipButton.tsx";
import UserDetails from "./UserDetails.tsx";

interface request {
    user: any,
    onClose: (refresh: boolean) => void
}

interface outerTab {
    name: "details" | "roles" | "reports" | "columns",
    title: "Details" | "Roles" | "Scheduled Reports" | "Column Security"
}

const outerTabs: outerTab[] = [{ name: "details", title: "Details" }, { name: "roles", title: "Roles" }, { name: "columns", title: "Column Security" }];
export default function UserRoleDrawer({ user, onClose }: request) {
    const [searchParams] = useSearchParams();
    const [userForm, setUserForm] = useState<UserFormRequest>();
    const [baseUser, setBaseUser] = useState<any>(); // Track the baseline user for comparison
    const { user: loggedInUser } = useContext(AuthContext);

    const visibleTabs: outerTab[] = IsAdminUser(loggedInUser)
        ? [{ name: "details", title: "Details" }, { name: "roles", title: "Roles" }, { name: "columns", title: "Column Security" }]
        : [{ name: "details", title: "Details" }, { name: "columns", title: "Column Security" }];

    // --- Original State for Default Profile ---
    const [assignedRoles, setAssignedRoles] = useState<RoleResponseModel[]>([]);
    const [groups, setGroups] = useState<RoleGroup[]>([]);

    const [prevItems, setPrevItems] = useState<{ prevRoles: any[], prevGroups: any[] }>({
        prevRoles: [],
        prevGroups: []
    });
    const [activeTab, setActiveTab] = useState<outerTab>({ name: "details", title: "Details" });
    const { dimensions, roles, setAllUsers } = useAppContext();
    const [roleDirty, setRoleDirty] = useState(false);
    const navigate = useNavigate();

    // --- Data Loading Logic ---
    const initAssignedRoles = (roles: RoleResponseModel[]) => {
        if (user.userID > 0) {
            GetUserRoles(user.userID).then((x) => {
                let tRoles: RoleResponseModel[] = [];
                GerUsersRoleGroup(user.userID).then((g) => {
                    for (var indx in x.data) {
                        let tRole = roles.find((r) => r.roleID == x.data[indx].roleID);
                        if (typeof tRole != 'undefined')
                            tRoles.push(tRole);
                    }
                    setGroups([...g.data]);
                    let pRoles = tRoles.map((r) => r.roleID != null ? r.roleID : 0);
                    let pGroups = g.data.map((x) => x.roleGroupID);
                    setPrevItems({
                        ...{
                            prevGroups: pGroups,
                            prevRoles: pRoles
                        }
                    });
                    setAssignedRoles(tRoles);
                })
            })
        }
        else if (user.userID == -2) { //For excel upload
            let tRoles: RoleResponseModel[] = [];
            let dims: string[] = [];
            for (var indx in roles) {
                let tRole = roles[indx];
                if (dims.indexOf(tRole.dataMapCode) == -1 && (typeof tRole != 'undefined')) {
                    tRoles.push(tRole);
                    dims.push(tRole.dataMapCode);
                }
            }
            setAssignedRoles([...tRoles]);
        }
    }

    useEffect(() => {
        rolesLoaded(roles);
    }, [user])

    const rolesLoaded = (roles: RoleResponseModel[]) => {
        initAssignedRoles(roles);
        if (user.userID == -2)
            return;
        let tRoles: RoleResponseModel[] = [];
        for (var dim in user.dimesionsRoles) {
            for (var indx in user.dimesionsRoles[dim]) {
                let curRole = user.dimesionsRoles[dim][indx];
                if (curRole.roleID > 0) {
                    let oRole = roles.find((r) => r.roleID == curRole.roleID);
                    if (typeof oRole != 'undefined')
                        tRoles.push(oRole)
                }
                else {
                    let tName = curRole.roleID == allAccessRoleID[dim] ? "Required all Access" : curRole.conditions;
                    let tempRole = {
                        roleID: curRole.roleID,
                        roleName: tName,
                        roleCode: "",
                        effectiveDate: "",
                        status: "",
                        roleType: "",
                        description: "",
                        dataMapCode: dim,
                        notes: "",
                        dimensionName: dim,
                        weight: 0,
                        createdBy: 0,
                        dimensionID: 0,
                        filterConditions: tName,
                        changed: false,
                        _source: "",
                        dirtyFields: []
                    }
                    tRoles.push(tempRole);
                }
            }
        }
        setAssignedRoles([...tRoles]);
    }

    const handleRoleUpdate = (updatedRoles: RoleResponseModel[], updatedGroups: RoleGroup[]) => {
        setRoleDirty(true);
        setAssignedRoles([...updatedRoles]);
        setGroups([...updatedGroups]);
    };

    const [actionMessage, setActionMessage] = useState<{ type: string | null, message: string, close: boolean }>({ type: null, message: "", close: false });
    const { showToast } = useToast();
    const { setSelectedTab } = useJoyride();

    useEffect(() => {
        setSelectedTab(activeTab.name);
    }, [activeTab]);

    useEffect(() => {
        let tUser = JSON.parse(JSON.stringify(user));
        if (user.userID == -2)
            tUser.userID = 0;
        setUserForm(tUser);
        setBaseUser(JSON.parse(JSON.stringify(tUser))); // Sync local base state
    }, [user])

    const handlePostSaveSuccess = (redirect: boolean) => {
        setActionMessage({ type: "success", message: "Saved successfully.", close: redirect });
        showToast({
            message: "Saved successfully",
            type: "success",
            duration: 5000
        });

        // 1. Reset the dirty tracking states so "Save" disables
        setRoleDirty(false);
        if (userForm) {
             setBaseUser(JSON.parse(JSON.stringify(userForm)));
        }
        
        // 2. Update prevItems so future changes compare correctly
        setPrevItems({
            prevGroups: groups.map((x) => x.roleGroupID),
            prevRoles: assignedRoles.map((r) => (r.roleID != null ? r.roleID : 0))
        });

        GetUsers({ userID: 0, userName: "", email: "", roleID: null, roleGroupID: null, managerUserID: null, appliationAccess: null }).then((u) => {
            setAllUsers([...u.data]);
        });
    }

    const SaveUser = (redirect: boolean) => {
        if (typeof userForm != 'undefined') {
            setActionMessage({ type: "progress", message: "Processing...", close: redirect });
            const updateGroups = (userId: number) => {
                setActionMessage({ type: "progress", message: "Updating Groups...", close: redirect });
                let gIds = groups.map((x) => x.roleGroupID);
                let pGroups = prevItems.prevGroups;
                AddRemoveUsersRoleGroup({ roleGroups: gIds, userID: userId, byUser: loggedInUser.userID, prevGroups: pGroups }).then((x) => {
                    handlePostSaveSuccess(redirect);
                })
            }
            const updateRole = (userId: number) => {
                let dRoles: { [key: string]: number[] } = {};
                setActionMessage({ type: "progress", message: "Applying role changes...", close: redirect });
                for (var indx in assignedRoles) {
                    let cRole = assignedRoles[indx];
                    if (cRole.roleID != null) {
                        if (typeof dRoles[cRole.dataMapCode] != 'undefined') {
                            dRoles[cRole.dataMapCode].push(cRole.roleID);
                        }
                        else
                            dRoles[cRole.dataMapCode] = [cRole.roleID];
                    }
                }
                let pRoles = prevItems.prevRoles;
                UpdateUserRoles({ userID: userId, userRoles: dRoles, byUser: loggedInUser.userID, prevRoles: pRoles }).then((x) => {
                    updateGroups(userId);
                })
            }
            
            userForm.dirtyFields = GetObjectDifferences(userForm, baseUser); // Compare with local baseUser
            if (userForm.userID == 0) {
                let mustPass = ["status", "managerUserID", "applicationAccess", "isApprover"];
                for (var indx in mustPass) {
                    if (userForm.dirtyFields.indexOf(mustPass[indx]) == -1)
                        userForm.dirtyFields.push(mustPass[indx]);
                }
            }
            userForm.byUser = loggedInUser.userID;
            UpsertUser(userForm).then((x) => {
                if (!isNaN(parseInt(x.data))) {
                    if (roleDirty)
                        updateRole(parseInt(x.data));
                    else {
                        handlePostSaveSuccess(redirect);
                    }
                }
                else {
                    showToast({
                        message: x.data,
                        type: "warning",
                        duration: 5000
                    })
                    setActionMessage({ type: "error", message: "Error", close: false });
                }
            })
        }
    }

    useEffect(() => {
        if (actionMessage.type != null && actionMessage.type != "progress") {
            const timer = setTimeout(() => {
                if (actionMessage.close)
                    onClose(true);
                setActionMessage({ type: null, message: "", close: false });
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [actionMessage]);


    const canSave = () => {
        let can = false;
        const allDimensionsAssigned = dimensions.every(dim =>
            assignedRoles.some(role => role.dataMapCode === dim.dimensionCode)
        );

        if (!allDimensionsAssigned) return false;

        const isValidEmail = (email?: string) => {
            if (!email) return false; // Fail if empty
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(email);
        };
        
        // Compare current form state with our baseline tracking state, NOT the stale user prop
        if (baseUser && !AreObjectsEquals(userForm, baseUser)) {
            if (
                userForm?.userName !== "" &&
                userForm?.userFirstName !== "" &&
                isValidEmail(userForm?.email)
            ) {
                can = true;
            }
        }
        else {
            can = roleDirty;
        }
        return can;
    }

    return (
        typeof userForm != 'undefined' &&
        <ComponentLayout
            instanceId={user.userID} objectType={AuditObjectTypes.User}
            exButtons={
                <AnimatePresence>
                    {actionMessage.type != null ? (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            style={{
                                margin: "0 auto",
                                width: "fit-content",
                                maxWidth: "90%",
                                pointerEvents: "none",
                            }}
                            className={`alert py-1 my-0 ${actionMessage.type === "progress" ? "alert-warning" : actionMessage.type === "success" ? "alert-success" : "alert-danger"
                                }`}
                        >
                            <SavingIndicator {...{ color: defaultTheme.colors.primary, style: "spinner", message: actionMessage.message, type: "saving" }} />
                        </motion.div>
                    ) :
                        <TooltipButton title={!canSave() ? "Ensure all user details are valid, changes have been made, and every Dimension has at least one role assigned." : ""}>
                            <div className="d-inline-flex justify-content-end gap-2">
                                {
                                    userForm.userID != 0 &&
                                    <button disabled={!canSave()} className={`j_u_save ${!canSave() ? "btn bg-secondary d-flex flex-row align-items-center gap-1" : "btn btn-primary d-flex flex-row align-items-center gap-1"}`} onClick={() => {
                                        SaveUser(false);
                                    }}>
                                        <FaSave />Save
                                    </button>
                                }
                                <button disabled={!canSave()} className={`j_u_sa_cl ${!canSave() ? "btn bg-secondary d-flex flex-row align-items-center gap-1 " : "btn btn-outline-primary d-flex flex-row align-items-center gap-1"}`} onClick={() => {
                                    SaveUser(true);
                                }}>
                                    <FaCheckCircle />Save & Close
                                </button>
                            </div>
                        </TooltipButton>
                    }
                </ AnimatePresence>}
            title={<div className='d-flex flex-row align-items-center'>
                <div className='fw-normal'>
                    {(user.userID > 0) ?
                        <div className="d-flex flex-row align-items-center gap-1 u_edit-name">Edit User: <b>{user.userName} </b><UserTypeBadge {...{ noTitle: false, userType: userForm.applicationAccess }} /></div> :
                        <div className="d-flex flex-row align-items-center gap-1">New User<UserTypeBadge {...{ noTitle: false, userType: userForm.applicationAccess }} /></div>
                    }
                </div>
                <span className='fs-3 px-2 mx-2 fw-normal' style={{ opacity: "0.2" }}>|</span>
                <button className='btn px-0 j_u_bBtn' onClick={() => {
                    if (searchParams.get("back")?.toLowerCase() == "profile")
                        navigate("/profile")
                    else
                        navigate("/users")
                }}>
                    <div className='d-flex flex-row align-items-center gap-1 fw-500 t-user-back-btn'>
                        <PiArrowBendUpLeft size={16} />
                        {
                            searchParams.get("back")?.toLowerCase() == "profile" ?
                                "Back to User Profile" :
                                "Back to all Users"
                        }
                    </div>
                </button>
            </div>} bodyHeight={300} subHeader={undefined} cBody={
                <motion.div
                    initial={{ x: 300 }}
                    animate={{ x: 0 }}
                    exit={{ x: 300 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    className="h-100 p-4 overflow-y-auto bg-theme-card"
                >
                    <div className="d-flex border-bottom mb-4">
                        {visibleTabs.map(tab => (
                            <button
                                key={tab.name}
                                onClick={() => setActiveTab(tab)}
                                className={`${"bg-theme-card px-3 py-2 position-relative"} j_u_${tab.name}`}
                                style={{
                                    borderWidth: "0px 0px 4px 0px",
                                    borderBottom: `4px solid ${activeTab.name === tab.name ? defaultTheme.colors.primary : 'transparent'}`,
                                    fontWeight: activeTab.name === tab.name ? 'bold' : 'normal',
                                    color: activeTab.name === tab.name ? defaultTheme.colors.primary : 'gray',
                                    display: visibleTabs.length > 1 ? 'block' : 'none'
                                }}
                            >
                                {tab.title}
                            </button>
                        ))}
                    </div>
                    <AnimatePresence mode="wait">
                        {activeTab.name === 'details' ?
                            <UserDetails formData={userForm} setFormData={(u) => { setUserForm(u) }} /> :
                            activeTab.name == "roles" ?
                                <div className="d-flex flex-column gap-3">
                                    {/* --- Persona Header & Selector --- */}
                                    {/* <div className="bg-white rounded border p-2">
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <div className="d-flex align-items-center gap-2 text-muted px-2">
                                                <PiIdentificationCardLight size={20} />
                                                <small className="fw-bold text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>User Personas</small>
                                            </div>
                                            {!showAddPersona && (
                                                <button onClick={() => setShowAddPersona(true)} className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1">
                                                    <FaPlus size={10} /> Add Persona
                                                </button>
                                            )}
                                        </div>

                                        <AnimatePresence>
                                            {showAddPersona && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="overflow-hidden border-bottom mb-2 pb-2"
                                                >
                                                    <div className="d-flex gap-2 align-items-end p-2 bg-light rounded">
                                                        <div className="flex-grow-1">
                                                            <label className="small text-muted mb-1">Persona Name</label>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                placeholder="e.g. Sales Manager"
                                                                value={newPersonaForm.name}
                                                                onChange={(e) => setNewPersonaForm({ ...newPersonaForm, name: e.target.value })}
                                                            />
                                                        </div>
                                                        <div className="flex-grow-1">
                                                            <label className="small text-muted mb-1">Description</label>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                placeholder="Context for this role set..."
                                                                value={newPersonaForm.description}
                                                                onChange={(e) => setNewPersonaForm({ ...newPersonaForm, description: e.target.value })}
                                                            />
                                                        </div>
                                                        <button onClick={handleAddPersona} disabled={!newPersonaForm.name} className="btn btn-sm btn-primary">
                                                            Add
                                                        </button>
                                                        <button onClick={() => setShowAddPersona(false)} className="btn btn-sm btn-light text-danger">
                                                            <FaTimes />
                                                        </button>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>

                                        <div className="d-flex gap-2 overflow-x-auto pb-1 px-1">
                                            <button
                                                onClick={() => setActivePersonaId(null)}
                                                className={`btn btn-sm d-flex align-items-center gap-2 ${activePersonaId === null
                                                    ? "btn-primary shadow-sm"
                                                    : "btn-light border text-muted"
                                                    }`}
                                                style={{ minWidth: '140px', transition: 'all 0.2s' }}
                                            >
                                                <FaUserTag />
                                                <div className="d-flex flex-column align-items-start" style={{ lineHeight: '1.1' }}>
                                                    <span>Primary Account</span>
                                                    <small style={{ fontSize: '0.65rem', opacity: 0.8 }}>Default Roles</small>
                                                </div>
                                            </button>

                                            {personas.map((persona) => (
                                                <motion.div
                                                    layout
                                                    key={persona.id}
                                                    initial={{ opacity: 0, scale: 0.9 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    className="position-relative"
                                                >
                                                    <button
                                                        onClick={() => setActivePersonaId(persona.id)}
                                                        className={`btn btn-sm d-flex align-items-center gap-2 ${activePersonaId === persona.id
                                                            ? "btn-primary shadow-sm"
                                                            : "btn-light border text-muted"
                                                            }`}
                                                        style={{ minWidth: '140px', paddingRight: '25px', transition: 'all 0.2s' }}
                                                    >
                                                        <PiIdentificationCardLight size={16} />
                                                        <div className="d-flex flex-column align-items-start" style={{ lineHeight: '1.1' }}>
                                                            <span className="text-truncate" style={{ maxWidth: '100px' }}>{persona.name}</span>
                                                            <small className="text-truncate" style={{ fontSize: '0.65rem', opacity: 0.8, maxWidth: '100px' }}>
                                                                {persona.description || "Persona"}
                                                            </small>
                                                        </div>
                                                    </button>
                                                    <button
                                                        className="position-absolute btn btn-link p-0 text-danger"
                                                        style={{ top: '2px', right: '5px', opacity: 0.6 }}
                                                        onClick={(e) => handleDeletePersona(persona.id, e)}
                                                        title="Remove Persona"
                                                    >
                                                        <span style={{ fontSize: '10px' }}><FaTrash /></span>
                                                    </button>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </div> */}

                                    {/* --- Role Configuration (Dynamic based on Selection) --- */}
                                    <RoleTabs
                                        key={"default"} // Force re-render on switch
                                        emptyMessage={"Assign roles to this user from Copy roles option or available roles section."}
                                        assigned={assignedRoles}
                                        updateAssigned={(r, g) => handleRoleUpdate(r, g)}
                                        groups={groups.map(x => x.roleGroupID).join(",")}
                                    />
                                </div>
                                : activeTab.name === "columns" ? (
                                    <UserColumnSecurity user={user} />
                                ) :
                                    <div>
                                        <UserReports {...{ user: user }} />
                                    </div>
                        }
                    </AnimatePresence >
                </motion.div >
            } />
    );
}